import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Dialog,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkRoundedIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MusicOffRoundedIcon from '@mui/icons-material/MusicOffRounded';
import NotesIcon from '@mui/icons-material/Notes';
import PublicIcon from '@mui/icons-material/Public';
import QueueMusicRoundedIcon from '@mui/icons-material/QueueMusicRounded';
import SearchRoundedIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EmptyState from '../../../../_helpers/reusableComponents/design/EmptyState';
import SectionCard from '../../../../_helpers/reusableComponents/design/SectionCard';
import logoGntunes from '../../../../img/logo4.svg';
import {
    motion,
    palette,
    radius,
    shadow,
    typography,
} from '../../../../_helpers/reusableComponents/design/designTokens';
import { SkeletonWaveCard } from '../../../../_helpers/reusablefunctions/SkeletonCard';
import { getStrPlatformImg } from '../../../../_helpers/reusablefunctions/getStreamingPlatform';
import { handleSongList } from '../../../../_services/creater/creatorSongList.service';
import {
    getSongDetail,
    listSongLyrics,
    scriptLabel,
} from '../../../../_services/creater/songContent.service';
import { handleRelatedPlatform } from '../../../../_services/creater/relatedPlatfom.service';
import { SHARE_SURFACE } from '../../../../_services/creater/shareLink.service';
import { getStreamingPlatforms } from '../../../../_services/creater/streamingPlatform.service';
import { updatePlateformSequence } from '../../../../_services/platform/platformSequence';
import { createPlaylist } from '../../../../_services/playlist/createPlaylist';
import { getAllPlaylistOfUser } from '../../../../_services/playlist/getAllPlaylistOfUser';
import { getSinglePlaylist } from '../../../../_services/playlist/getSinglePlaylist';
import { removeSongFromPlaylist } from '../../../../_services/playlist/removeSongFromPlaylist';
import { searchPlaylist } from '../../../../_services/playlist/searchPlaylist';
import { updatePlaylist } from '../../../../_services/playlist/updatePlaylist';
import { getGenre, getLanguage } from '../../../../_services/profile';
import { moodRequest } from '../../../../_services/profile/mood.service';
import { handleTagList } from '../../../../_services/tags/tagList.service';
import { formSnackbar } from '../../../../_services/_constants/snackbar.constant';
import { setSnackBar, setSnackBarMessage, setSnackBarVariant } from '../../../../redux';
import AddMusicDetailsModal from '../songList/AddMusicDetailsModal';
import AddPlatforms from '../songList/AddStreamingPlatforms';
import { StreamingPlatformContext } from '../songList/AddSong';
import { LinkAnalytics, ShareLinkDialog } from '../shareLink/ShareLinkPage';
import SongLyricsEditor from '../songList/SongLyricsEditor';
import SongSeoFields from '../songList/SongSeoFields';

// The song list serializes the poster under four different names depending on
// when the row was written and by which uploader. Same order the song card
// uses, so the thumbnail here is the one the creator just saw on the card.
const posterOf = (row) => {
    if (!row) return null;
    if (row.songPoster100x100) return row.songPoster100x100;
    if (row.songPoster_url) return row.songPoster_url;
    if (row.songPoster) return `${process.env.REACT_APP_BASEURL_MEDIA}/media/${row.songPoster}`;
    if (row.cover_image) return `${process.env.REACT_APP_SONGAPIURL3_MEDIA}${row.cover_image}`;
    return null;
};

// The Song Hub link-bar button, to the pixel. `gn-actionbtn` is the product's
// ONE filled red call to action and there is no such action on this screen —
// everything here saves itself — so every button below is this quiet one.
const linkBarBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: `1px solid ${palette.line}`,
    borderRadius: `${radius.sm}px`,
    bgcolor: palette.surface,
    color: palette.inkSoft,
    fontSize: 12.5,
    fontWeight: 700,
    padding: '7px 12px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: `background-color ${motion.base}, color ${motion.base}, border-color ${motion.base}`,
    '&:hover': { bgcolor: palette.surfaceAlt, color: palette.ink, borderColor: palette.hint },
};

const GhostBtn = ({ children, component = 'button', sx = {}, ...rest }) => (
    <Box
        component={component}
        // Only a real button takes a type. The same control is rendered as an
        // anchor where it opens the live page, and `type` on an <a> means
        // something else entirely.
        type={component === 'button' ? 'button' : undefined}
        sx={{
            ...linkBarBtn,
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Box>
);

// A stored value can be an id or the nested object the list serializer returns,
// depending on which of the two reads the row came from. Both resolve through
// the master list, so the preview never prints a bare number.
const nameOf = (list, value, key) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'object') return value[key] || '';
    const found = (list || []).find((row) => String(row.id) === String(value));
    return found ? found[key] : '';
};

// Which tab is showing is part of the address, not just component state: a
// refresh, a bookmark or a link pasted to someone else all landed on the first
// tab otherwise, however deep into the screen you were.
const readTab = (search, allowed, fallback) => {
    const wanted = new URLSearchParams(search).get('tab');
    return allowed.indexOf(wanted) >= 0 ? wanted : fallback;
};

// In the order the song is filled in, not the order the public page shows it:
// what the song IS, where it can be played, which of your playlists it belongs
// to, then the two panes of text about it.
const TABS = ['details', 'platform', 'playlist', 'lyrics', 'seo', 'insights'];

// Which panel of the PUBLIC song page each tab is editing. The phone on the
// right follows it, so the preview is always showing the thing being edited —
// gnartist-profile puts Platform, About, Lyrics and Credits behind one pill
// bar, and Details and Search & social both feed its About panel.
// The pills under the cover on the public song page.
// The back arrow's box and the gap after it. The breadcrumb line is indented by
// their sum so it starts under the TITLE rather than under the arrow, which is
// what it did when the arrow had a border to sit inside.
const BACK_SIZE = 32;
const BACK_GAP = 8;

const PHONE_TABS = [
    { key: 'platform', label: 'Platform' },
    { key: 'about', label: 'About' },
    { key: 'lyrics', label: 'Lyrics' },
    { key: 'credits', label: 'Credits' },
];

// The `song_credit` columns in the order a record sleeve lists them, the same
// order and the same labels gnartist-profile uses — who made the record first,
// who was hired second, the label last.
const CREDIT_FIELDS = [
    ['primary_artists', 'Primary artists'],
    ['featuring_artists', 'Featuring'],
    ['composers', 'Composers'],
    ['lyricists', 'Lyricists'],
    ['producers', 'Producers'],
    ['actors', 'Actors'],
    ['music_labels', 'Music label'],
];

// Which variant a lyrics credit belongs to. The language alone is ambiguous the
// moment a song carries one language in two scripts — the common case, an
// original beside its romanisation — so the script is always named too.
const variantLabel = (row) => {
    const language = (row && row.language_name) || '';
    const script = row && row.script ? scriptLabel(row.script) : '';
    const base = language && script ? `${language} (${script})` : (language || script);
    return row && row.is_translation ? `${base} — translation` : base;
};

const PHONE_PANEL = {
    details: 'about',
    platform: 'platform',
    playlist: 'about',
    lyrics: 'lyrics',
    seo: 'about',
    insights: 'about',
};

const prettyDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * The song as its public page shows it, in a phone, pinned beside the form.
 *
 * NOT an iframe of the real page: gntunes.com answers with
 * `X-Frame-Options: SAMEORIGIN`, so the creator app cannot frame it at all —
 * and even where it could, an iframe only catches up on a reload. This is drawn
 * from the same state the form is editing, so a genre chosen on the left
 * appears on the right as it is chosen, which is the whole point of having the
 * phone there.
 *
 * It mirrors what the microsite renders (`gn-sparkle`): cover, title, the
 * Platform / About / Lyrics strip, and the facts list underneath.
 */
const PhonePreview = ({
    song, poster, banner, genre, moodList, languageList, loading,
    platforms = [], panel = 'about', onPanel, lyrics = [], lyricsLoading = false,
}) => {
    // Which lyrics variant the panel is showing. Clamped rather than reset when
    // the set changes, so a variant deleted one tab over cannot leave this
    // pointing past the end of the list.
    const [variant, setVariant] = useState(0);
    const title = song ? (song.songTitle || 'Untitled song') : '';
    const artist = song ? (song.artists || '') : '';

    // A row with a credit but no content is not lyrics — it is a credit someone
    // saved without the words — so it is dropped here rather than out of
    // `lyrics`, where the Credits panel still needs it.
    const lyricTexts = (lyrics || []).filter((row) => String((row && row.content) || '').trim());
    // The API orders variants with the default first, so index 0 is the one the
    // song page opens on and a visitor reads first.
    const lyric = lyricTexts[Math.min(variant, Math.max(lyricTexts.length - 1, 0))];

    // Two sources, one panel, exactly as the public page joins them. The release
    // flow writes `song_credit`; a song added straight to the hub never goes
    // through it, so for most songs the panel is filled by the Credit box on
    // each lyrics variant instead — who wrote or translated that version.
    const credits = [
        ...CREDIT_FIELDS
            .map(([name, label]) => {
                const value = song && song.song_credit && song.song_credit[name];
                return value ? { key: name, label, value: String(value) } : null;
            })
            .filter(Boolean),
        ...(lyrics || [])
            .filter((row) => row && row.credit)
            .map((row) => ({ key: `lyric-${row.id}`, label: variantLabel(row), value: String(row.credit) })),
    ];

    const facts = song ? [
        artist && { label: artist.indexOf(',') >= 0 ? 'Artists' : 'Artist', value: artist },
        song.release_date && { label: 'Released', value: prettyDate(song.release_date) },
        song.record_label && { label: 'Label', value: song.record_label },
        { label: 'Genre', value: nameOf(genre, song.genre, 'genre') },
        { label: 'Mood', value: nameOf(moodList, song.mood, 'mood') },
        { label: 'Language', value: nameOf(languageList, song.language, 'language') },
    ].filter((row) => row && row.value) : [];

    return (
        <div className="phone">
            <div className="phone__frame">
                <div className="phone__notch" />
                <div className="phone__screen">
                    <div
                        className="phone__hero"
                        style={banner ? { backgroundImage: `url("${banner}")` } : undefined}
                    >
                        <span className="phone__burger">
                            <span /><span /><span />
                        </span>
                        <div className="phone__heroTitle">{loading ? '…' : title}</div>
                        <div className="phone__crumbs">
                            <span>My Hub</span>
                            <span className="phone__crumbSep">◆</span>
                            <span>Music</span>
                            {!loading && title && (
                                <>
                                    <span className="phone__crumbSep">◆</span>
                                    <span>{title}</span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="phone__body">
                        {loading ? (
                            <Stack spacing={1.5}>
                                <Skeleton variant="rounded" height={140} />
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="rounded" height={90} />
                            </Stack>
                        ) : (
                            <>
                                <div className="phone__cover">
                                    {poster
                                        ? <img src={poster} alt="" />
                                        : <span className="phone__coverEmpty">No cover art</span>}
                                </div>

                                {song.is_explicit && <div className="phone__explicit">EXPLICIT</div>}

                                {/* The strip the song page puts under the cover,
                                    and the one part of this phone that is wired
                                    rather than drawn: it switches the panel
                                    below, as the real one does. Otherwise the
                                    lit pill follows the tab being edited, so
                                    the preview is showing the panel the fields
                                    on the left feed. */}
                                <div className="phone__tabs">
                                    {PHONE_TABS.map((row) => (
                                        <button
                                            key={row.key}
                                            type="button"
                                            className={`phone__tab${panel === row.key ? ' is-on' : ''}`}
                                            aria-pressed={panel === row.key}
                                            onClick={() => onPanel && onPanel(row.key)}
                                        >
                                            {row.label}
                                        </button>
                                    ))}
                                </div>

                                {panel === 'credits' ? (
                                    lyricsLoading ? (
                                        <Skeleton variant="rounded" height={120} />
                                    ) : credits.length === 0 ? (
                                        <div className="phone__empty">
                                            No credits yet — this tab will not appear on your public page.
                                        </div>
                                    ) : (
                                        <div className="phone__card">
                                            {credits.map((row) => (
                                                <div className="phone__fact" key={row.key}>
                                                    <span>{row.label}</span>
                                                    <strong>{row.value}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : panel === 'lyrics' ? (
                                    /* What a first-time visitor reads: the
                                       variant the creator put first, which is
                                       the one the song page opens on. */
                                    lyricsLoading ? (
                                        <Skeleton variant="rounded" height={120} />
                                    ) : !lyric ? (
                                        <div className="phone__empty">
                                            No lyrics yet — this tab will not appear on your public page.
                                        </div>
                                    ) : (
                                        <div className="phone__card">
                                            {/* The switcher appears only for a
                                                song that HAS more than one
                                                variant, as the public page's
                                                does — one set of lyrics under a
                                                lone button is a control that can
                                                do nothing. Each pill names its
                                                script as well as its language:
                                                an original beside its
                                                romanisation is two variants of
                                                one language, and "Hindi" twice
                                                tells you nothing. */}
                                            {lyricTexts.length > 1 && (
                                                <div className="phone__variants">
                                                    {lyricTexts.map((row, index) => (
                                                        <button
                                                            key={row.id}
                                                            type="button"
                                                            className={`phone__variant${index === variant ? ' is-on' : ''}`}
                                                            aria-pressed={index === variant}
                                                            onClick={() => setVariant(index)}
                                                        >
                                                            {variantLabel(row)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {/* The rule that draws the line above
                                                About belongs to the facts over
                                                it; with no switcher there is
                                                nothing for it to divide. */}
                                            <p
                                                className="phone__about"
                                                style={lyricTexts.length > 1 ? undefined : { border: 0, margin: 0, paddingTop: 0 }}
                                            >
                                                {lyric.content}
                                            </p>
                                        </div>
                                    )
                                ) : panel === 'platform' ? (
                                    /* The buttons the public page stacks under
                                       PLATFORM, in the order they are dragged
                                       into on the left. A link with no platform
                                       chosen yet is skipped rather than drawn as
                                       a blank button. */
                                    platforms.filter((row) => row && row.id).length === 0 ? (
                                        <div className="phone__empty">
                                            Add a streaming platform and it appears here.
                                        </div>
                                    ) : platforms.filter((row) => row && row.id).map((row) => (
                                        <div className="phone__song" key={row.id}>
                                            <img
                                                className="phone__songArt"
                                                src={getStrPlatformImg(row.streamingPlatformKey)}
                                                alt=""
                                            />
                                            <span className="phone__songBody">
                                                <span className="phone__songTitle">
                                                    {row.streamingPlatformName || 'Streaming platform'}
                                                </span>
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="phone__card">
                                        {facts.length === 0 && (
                                            <div className="phone__empty">
                                                Fill in the details and they appear here.
                                            </div>
                                        )}
                                        {facts.map((row) => (
                                            <div className="phone__fact" key={row.label}>
                                                <span>{row.label}</span>
                                                <strong>{row.value}</strong>
                                            </div>
                                        ))}
                                        {song.about && <p className="phone__about">{song.about}</p>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="phone__footer">
                        <img src={logoGntunes} alt="GNTunes" className="phone__footerLogo" />
                        <div className="phone__footerCta">Create Your Profile</div>
                        <div className="phone__footerLegal">
                            By using this service you agree to our <b>Privacy Policy</b> and <b>Terms</b> Of Use.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


/**
 * Where this song can be played — the PLATFORM panel of the public song page.
 *
 * The same editor the song card opened in a right-hand drawer, on the screen
 * the rest of the song is edited on. It belongs here: it is the FIRST thing
 * gnartist-profile shows a listener, and the one thing the profile toggle
 * refuses to turn on without — a field that important should not live behind a
 * kebab menu on another screen.
 *
 * `AddPlatforms` is reused as it stands, context and all. It owns the platform
 * dropdown, the link box, the embed switch and the delete confirmation, and a
 * second copy of that would be a second set of bugs.
 */
const PlatformPane = ({
    songId, disabled, rows, setRows, catalog, setCatalog, catalogCopy, loading, reload,
}) => {
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.userData.userData);
    // A blank card is already open and unsaved. AddPlatforms clears this
    // through `addNewDisabledHandler` once the row is saved or dropped.
    const [addDisabled, setAddDisabled] = useState(false);

    const notify = (message, variant, hold = 2000) => {
        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(message));
        dispatch(setSnackBarVariant(variant));
        setTimeout(() => dispatch(setSnackBar(false)), hold);
    };

    // The dropdown of platforms still free to choose. AddPlatforms calls this
    // as it takes, frees and swaps them, so one song never offers the same
    // platform twice.
    const handleAllPlatformList = (data, action) => {
        let remaining = [...catalog];
        if (action === 'remove') {
            setCatalog(remaining.filter(
                (entry) => !data.some((taken) => entry?.platformId === taken.streamingPlatform)
            ));
        } else if (action === 'add') {
            const freed = catalogCopy.find((item) => item?.platformId === data.streamingPlatform);
            setCatalog([...catalog, freed]);
        } else if (action === 'update') {
            remaining = remaining.filter((item) => item?.platformId !== data?.latestValue?.platformId);
            const freed = catalogCopy.find((item) => item?.platformId === data.previousValue?.platformId);
            setCatalog([...remaining, freed]);
        }
    };

    const handleAddNew = () => {
        setRows([{}, ...rows]);
        setAddDisabled(true);
    };

    const deletePlatformHandler = (event, songid, relatedPlatformLinkId, index, relatedPlatformData) => {
        const copy = [...rows];
        if (copy.length === 0) return;
        setRows(copy.filter((item) => item.id !== relatedPlatformLinkId));
        setAddDisabled(false);
        // No id means the blank card was never saved: there is nothing on the
        // server to delete, and dropping it from the list is the whole job.
        if (relatedPlatformLinkId === undefined) return;
        notify(formSnackbar.relatedPlatform.relatedPlatformDelete, 'info');
        if (JSON.stringify(copy[0]) === '{}') setAddDisabled(true);
        handleRelatedPlatform(
            { userId: userData.id, relatedPlatformLinkId, songId: songid },
            'DELETE'
        ).then((response) => {
            if (response && (response.status === 200 || response.status === 204)) {
                notify(formSnackbar.relatedPlatform.relatedPlatformDeleteSuccess, 'success');
                handleAllPlatformList(relatedPlatformData, 'add');
            } else {
                notify(formSnackbar.errors.errorMsg, 'error');
            }
        }).catch(() => notify(formSnackbar.errors.errorMsg, 'error'));
    };

    // The order here IS the order of the buttons on the public page, so a drop
    // is saved immediately rather than behind a Save.
    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(rows);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setRows(items);
        updatePlateformSequence(userData.id, { platform_ids: items.map((row) => row.id).join(',') })
            .then((response) => {
                if (!response || (response.status !== 200 && response.status !== 201 && response.status !== 204)) {
                    notify(formSnackbar.errors.errorMsg, 'error');
                }
            })
            .catch(() => notify(formSnackbar.errors.errorMsg, 'error'));
    };

    return (
        <SectionCard
            title="Streaming platforms"
            subtitle="Where this song can be played. These become the buttons under Platform on your public song page, in this order."
            // The rows AddPlatforms draws are white cards of their own, so the
            // body behind them is the page grey — white on white left them with
            // nothing but a shadow to stand on.
            bodySx={{ bgcolor: palette.surfaceAlt }}
            actions={(
                <button
                    type="button"
                    className="gn-actionbtn"
                    disabled={disabled || addDisabled || catalog.length === 0}
                    onClick={handleAddNew}
                >
                    ADD NEW
                </button>
            )}
        >
            {loading ? (
                [1, 2, 3].map((i) => <SkeletonWaveCard key={i} />)
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={<LibraryMusicOutlinedIcon sx={{ fontSize: 32 }} />}
                    title="No streaming platforms yet"
                    description="Add the Spotify, YouTube or Apple Music link for this song. Your public page needs at least one before the song can go on your profile."
                />
            ) : (
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="song-platforms">
                        {(dropProvided) => (
                            <div {...dropProvided.droppableProps} ref={dropProvided.innerRef}>
                                <StreamingPlatformContext.Provider
                                    value={{
                                        allPlatfromList: catalog,
                                        handleAllPlatformList,
                                        allPlatfromListCopy: catalogCopy,
                                    }}
                                >
                                    {rows.map((row, index) => (
                                        <Draggable
                                            key={row.id ?? index}
                                            draggableId={String(row.id ?? index)}
                                            index={index}
                                        >
                                            {(dragProvided) => (
                                                <AddPlatforms
                                                    deletePlatformHandler={deletePlatformHandler}
                                                    reRenderPlatformHandler={reload}
                                                    addNewDisabledHandler={() => setAddDisabled(false)}
                                                    cardData={row}
                                                    songId={songId}
                                                    cardId={row.id}
                                                    index={index}
                                                    allRelatedPlatforms={rows}
                                                    provided={dragProvided}
                                                />
                                            )}
                                        </Draggable>
                                    ))}
                                </StreamingPlatformContext.Provider>
                                {dropProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </SectionCard>
    );
};

// One page of playlists is enough for the question this panel answers — "which
// of mine is this song in?" — and the search box reaches the rest.
const PLAYLIST_PAGE = 20;

/**
 * Which of the creator's playlists this song belongs to.
 *
 * The song card's "Add playlist" was a one-way door: a modal that could put the
 * song into a playlist and never showed you where it already was, so the only
 * way to find out was to open every playlist in turn. Here the answer is the
 * screen — each playlist is a checkbox, already ticked if the song is in it,
 * and unticking takes it out.
 *
 * Membership costs one read per playlist listed: the list endpoint serializes a
 * song COUNT and not the songs, so there is nothing else to read it from. They
 * go out together and the boxes settle a moment after the names.
 */
// An admin-created song is read-only on every other tab, but not here: the
// playlists are the creator's own, and which of them a song sits in is their
// call whoever added the song. The old modal allowed it too.
const PlaylistPane = ({ songId }) => {
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.userData.userData);
    const userId = userData && userData.id;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Which playlists hold the song. Null while the membership reads are still
    // out, so a box is never drawn unticked before the answer is in.
    const [holding, setHolding] = useState(null);
    const [query, setQuery] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    const notify = (message, variant, hold = 2000) => {
        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(message));
        dispatch(setSnackBarVariant(variant));
        setTimeout(() => dispatch(setSnackBar(false)), hold);
    };

    const load = useCallback(async (search) => {
        if (!userId) return;
        setLoading(true);
        setHolding(null);
        const response = search
            ? await searchPlaylist({ search, user: userId })
            : await getAllPlaylistOfUser({ search: userId, limit: PLAYLIST_PAGE, offset: 0 });
        const list = (response && response.data && response.data.results) || [];
        if (!alive.current) return;
        setRows(list);
        setLoading(false);

        const answers = await Promise.all(
            list.map((row) => getSinglePlaylist({ id: row.playlist_id }))
        );
        if (!alive.current) return;
        const found = [];
        answers.forEach((answer, index) => {
            const playlist = answer && answer.data && answer.data.data && answer.data.data[0];
            const songs = (playlist && playlist.song) || [];
            if (songs.some((row) => String(row.id) === String(songId))) {
                found.push(list[index].playlist_id);
            }
        });
        setHolding(found);
    }, [userId, songId]);

    // Typing waits 400ms, the same beat the old modal used, so a name is not
    // searched letter by letter.
    useEffect(() => {
        const trimmed = query.trim();
        const run = setTimeout(() => { load(trimmed || undefined); }, trimmed ? 400 : 0);
        return () => clearTimeout(run);
    }, [query, load]);

    const isIn = (playlistId) => Boolean(holding && holding.indexOf(playlistId) >= 0);

    const toggle = async (row) => {
        const playlistId = row.playlist_id;
        const held = isIn(playlistId);
        setBusyId(playlistId);
        const response = held
            ? await removeSongFromPlaylist(playlistId, { song_ids: String(songId) })
            : await updatePlaylist(playlistId, { playlist_song: songId });
        if (!alive.current) return;
        setBusyId(null);
        if (response && (response.status === 200 || response.status === 201)) {
            setHolding((prev) => {
                const next = prev ? [...prev] : [];
                return held ? next.filter((id) => id !== playlistId) : [...next, playlistId];
            });
            notify(
                held ? formSnackbar.playlist.songRemove : formSnackbar.playlist.playlistUpdate,
                'success'
            );
        } else {
            notify(formSnackbar.errors.errorMsg, 'error');
        }
    };

    const create = async () => {
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        const response = await createPlaylist({
            playlist_name: name,
            playlist_song: songId,
            user: userId,
        });
        if (!alive.current) return;
        setCreating(false);
        if (response && (response.status === 200 || response.status === 201)) {
            setNewName('');
            notify(formSnackbar.playlist.playlistCreate, 'success');
            // The new playlist is not in the list yet, and it already holds the
            // song — both are answered by asking again.
            load(query.trim() || undefined);
        } else {
            notify(formSnackbar.errors.errorMsg, 'error');
        }
    };

    return (
        <SectionCard
            title="Playlists"
            subtitle="The playlists on your profile that this song is part of. Tick one to add the song, untick to take it out."
        >
            <TextField
                fullWidth
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search your playlists"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ fontSize: 18, color: palette.muted }} />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            {loading ? (
                <Stack spacing={1}>
                    {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
                </Stack>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={<QueueMusicRoundedIcon sx={{ fontSize: 32 }} />}
                    title={query.trim() ? 'No playlist by that name' : 'No playlists yet'}
                    description={query.trim()
                        ? 'Nothing of yours matches that. Clear the search to see them all.'
                        : 'Create one below and this song goes into it.'}
                />
            ) : (
                <Stack spacing={0.5}>
                    {rows.map((row) => {
                        const held = isIn(row.playlist_id);
                        return (
                            <Box
                                key={row.playlist_id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: `${radius.sm}px`,
                                    transition: `background-color ${motion.base}`,
                                    '&:hover': { bgcolor: palette.surfaceAlt },
                                }}
                            >
                                {busyId === row.playlist_id
                                    ? <Box sx={{ width: 42, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
                                    : (
                                        <Checkbox
                                            size="small"
                                            checked={held}
                                            // Until the membership reads answer,
                                            // the boxes are not yet telling the
                                            // truth, so they cannot be pressed.
                                            disabled={holding === null}
                                            onChange={() => toggle(row)}
                                            inputProps={{ 'aria-label': `${held ? 'Remove from' : 'Add to'} ${row.playlist_name}` }}
                                        />
                                    )}
                                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13.5,
                                            fontWeight: 600,
                                            color: palette.ink,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={row.playlist_name}
                                    >
                                        {row.playlist_name}
                                    </Typography>
                                    <Typography sx={{ ...typography.caption }}>
                                        {row.playlist_song_count > 0
                                            ? `${row.playlist_song_count} song${row.playlist_song_count === 1 ? '' : 's'}`
                                            : 'No songs yet'}
                                    </Typography>
                                </Box>
                                <Tooltip arrow title={row.privacy_status ? 'Public playlist' : 'Private playlist'}>
                                    <Box sx={{ display: 'flex', color: palette.muted, flexShrink: 0 }}>
                                        {row.privacy_status
                                            ? <PublicIcon sx={{ fontSize: 17 }} />
                                            : <LockOutlinedIcon sx={{ fontSize: 17 }} />}
                                    </Box>
                                </Tooltip>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {/* A playlist made here always starts with this song in it, which is
                the only reason to be making one from a song's page. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') create(); }}
                    placeholder="New playlist name"
                    inputProps={{ maxLength: 150 }}
                    helperText={`${newName.length}/150`}
                />
                <button
                    type="button"
                    className="gn-actionbtn"
                    style={{ whiteSpace: 'nowrap' }}
                    disabled={creating || newName.trim().length === 0}
                    onClick={create}
                >
                    {creating
                        ? <CircularProgress size={18} color="inherit" />
                        : <><AddRoundedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />CREATE</>}
                </button>
            </Box>
        </SectionCard>
    );
};

/**
 * Everything about one song that is not its platforms — the release metadata,
 * its lyrics variants, and the listing search engines and social cards use.
 *
 *   /songhub/music/:slug
 *
 * These three panes used to expand inside the song card, which put a form
 * three tabs deep inside a row of a draggable list: the fields were never more
 * than half a card wide, opening one pushed every song below it off screen, and
 * nothing about the song being edited could be linked to or reopened. They are
 * a screen, and this is it — the same shape as the short-URL page, reached from
 * the icon immediately left of the short-URL icon on the card.
 */
const SongDetailsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.userData.userData);
    const userId = userData && userData.id;
    const { slug } = useParams();

    // The route carries the SLUG, because that is the half of the URL a creator
    // recognises. Navigating from the song card hands the whole list row over in
    // router state, which costs nothing and saves the lookup below. A cold load
    // — a refresh, a bookmark, a link pasted into a second tab — has no state,
    // so the slug is matched against the creator's own songs instead.
    const [row, setRow] = useState(() => (location.state && location.state.song) || null);
    const [lookupFailed, setLookupFailed] = useState(false);

    // The same song through the DETAIL serializer. `about`, `release_date`,
    // `record_label` and `is_explicit` exist ONLY there, so the list row this
    // page was handed cannot fill the form on its own.
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(true);

    const [tab, setTab] = useState(() => readTab(location.search, TABS, 'details'));

    // `replace`, not `push`: a tab is a view of one screen, and pushing would
    // put every glance at the preview between the creator and the page they
    // actually came from. The router state rides along so the Back button keeps
    // knowing where that was.
    const changeTab = (value) => {
        setTab(value);
        history.replace({
            pathname: location.pathname,
            search: value === 'details' ? '' : `?tab=${value}`,
            state: location.state,
        });
    };

    // The song's address and its tags. They used to be edited on the list row,
    // which meant a slug — the thing every share of this song depends on — was
    // one stray keystroke away on a card built for scanning. Seeded from the row
    // once it resolves, saved through the same endpoint as everything else here.
    const [slugValue, setSlugValue] = useState('');
    const [slugError, setSlugError] = useState('');
    const [tags, setTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [tagError, setTagError] = useState('');

    const [genre, setGenre] = useState([]);
    const [moodList, setMoodList] = useState([]);
    const [languageList, setLanguageList] = useState([]);
    const [listsLoading, setListsLoading] = useState(true);

    // The song's streaming links, and the catalogue of platforms still free to
    // choose. They live up here rather than inside the Platform tab because the
    // phone on the right draws them too — the preview would otherwise be blank
    // until the tab had been opened once. `catalogCopy` is the full list, kept
    // apart so a platform freed by a delete can be put back into the dropdown.
    const [platforms, setPlatforms] = useState([]);
    const [platformCatalog, setPlatformCatalog] = useState([]);
    const [platformCatalogCopy, setPlatformCatalogCopy] = useState([]);
    const [platformsLoading, setPlatformsLoading] = useState(true);

    // Which panel the phone is showing, when the creator has picked one by
    // pressing a pill on the phone itself. Null means it is following the tab,
    // which is what it goes back to the moment another tab is opened — the
    // preview is meant to be showing the thing being edited, and a pinned panel
    // that outlives the tab it was pinned on stops doing that.
    const [shareOpen, setShareOpen] = useState(false);
    // Below 1200px the rail beside the form has nowhere to stand, so the phone
    // moves behind a button. Same phone, same props — a second, smaller preview
    // would be a second thing to keep true.
    const [previewOpen, setPreviewOpen] = useState(false);

    // Whether this song is on the public profile. Seeded from the row the way
    // the slug and the tags are, and moved optimistically: the eye is a switch,
    // and a switch that waits for a round trip before it moves reads as broken.
    const [visible, setVisible] = useState(false);
    const [visibleSaving, setVisibleSaving] = useState(false);

    const [phonePanel, setPhonePanel] = useState(null);
    const [lyrics, setLyrics] = useState([]);
    const [lyricsLoading, setLyricsLoading] = useState(false);

    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const songId = row ? row.id : undefined;

    // The list row merged with the detail read, detail winning. Both halves are
    // needed: `is_admin_created` — what makes an admin-created song read-only —
    // rides on the list row.
    const song = row ? (detail ? { ...row, ...detail } : row) : null;
    const songTitle = (song && song.songTitle) || (location.state && location.state.songTitle) || '';
    const isDisabled = Boolean(song && song.is_admin_created);
    const linkSlug = (song && song.slug) || songId;

    // ------------------------------------------------------------- the song

    useEffect(() => {
        if (row) return undefined;
        let cancelled = false;
        setLookupFailed(false);
        handleSongList({ userId: userData && userData.id }, 'GETBYUSER', 200, 0)
            .then((response) => {
                if (cancelled) return;
                const results = (response && response.data && response.data.results) || [];
                // Songs saved before slugs existed are addressed by id, and a
                // bare number should keep working rather than 404.
                const match = results.find((item) => String(item.slug) === String(slug))
                    || results.find((item) => String(item.id) === String(slug));
                if (match) {
                    setRow(match);
                    return;
                }
                setLookupFailed(true);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, row, userData && userData.id]);

    // `silent` is what a re-read after a save passes. Without it the skeleton
    // would come back for the length of that request, unmounting the form the
    // creator is still typing in — every blur would blink the whole panel and
    // drop the caret.
    const loadDetail = useCallback((silent) => {
        if (songId === undefined) return;
        if (!silent) setDetailLoading(true);
        getSongDetail(songId).then((response) => {
            if (!mounted.current) return;
            const body = response && response.data;
            // The viewset answers `{ data: [song] }`; a plain object is accepted
            // too, so a shape change upstream degrades to "no extra fields"
            // rather than a crash inside the form.
            const detailRow = Array.isArray(body && body.data) ? body.data[0] : body;
            if (detailRow && typeof detailRow === 'object') setDetail(detailRow);
            setDetailLoading(false);
        });
    }, [songId]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    // Seeded from the ROW, not the detail read: both carry them, and the row is
    // there first. Keyed on the song so switching songs re-seeds, and a save
    // does not bounce the value back under the cursor.
    useEffect(() => {
        if (songId === undefined || !row) return;
        setSlugValue(row.slug || '');
        setTags(row.tags ? String(row.tags).split(',').filter(Boolean) : []);
        setVisible(Boolean(row.visible));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [songId]);

    useEffect(() => {
        let cancelled = false;
        handleTagList({}, 'GET').then((response) => {
            if (cancelled || !mounted.current) return;
            setTagOptions(response && response.status === 200 ? response.data.data : []);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // ---------------------------------------------------- the dropdown lists

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            getGenre().then((res) => res.data.data).catch(() => []),
            getLanguage().then((res) => (res.data ? res.data.data : [])).catch(() => []),
            moodRequest().then((res) => res.data.data).catch(() => []),
        ]).then(([genreRows, languageRows, moodRows]) => {
            if (cancelled || !mounted.current) return;
            setGenre(genreRows || []);
            setLanguageList(languageRows || []);
            setMoodList(moodRows || []);
            setListsLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    // -------------------------------------------------------- the platforms

    const loadPlatforms = useCallback(() => {
        if (songId === undefined || !userId) {
            setPlatformsLoading(false);
            return;
        }
        setPlatformsLoading(true);
        handleRelatedPlatform({ songId, userId }, 'GETBYSONG')
            .then((response) => {
                if (!mounted.current) return;
                const list = (response && response.status === 200 && response.data.data) || [];
                // Sorted the way the public page stacks them, so the phone and
                // the editor agree with the site before anything is dragged.
                setPlatforms([...list].sort(
                    (a, b) => parseInt(a.platform_order, 10) - parseInt(b.platform_order, 10)
                ));
            })
            .catch(() => {})
            .finally(() => { if (mounted.current) setPlatformsLoading(false); });
    }, [songId, userId]);

    useEffect(() => { loadPlatforms(); }, [loadPlatforms]);

    // ------------------------------------------- the lyrics behind the phone

    const phoneShowing = phonePanel || PHONE_PANEL[tab] || 'about';

    useEffect(() => { setPhonePanel(null); }, [tab]);

    // Read when the Lyrics or Credits panel is actually looked at, and again
    // every time it is come back to: they are edited one tab over, and a stale
    // preview of them is worse than none. Credits needs the same read — the
    // Credit box on each variant is where most songs' credits come from.
    // Nothing is read for a creator who opens neither.
    useEffect(() => {
        if ((phoneShowing !== 'lyrics' && phoneShowing !== 'credits') || songId === undefined) return;
        let cancelled = false;
        setLyricsLoading(true);
        listSongLyrics(songId).then((response) => {
            if (cancelled || !mounted.current) return;
            setLyrics((response && response.status === 200 && response.data.data) || []);
        }).catch(() => {}).finally(() => {
            if (!cancelled && mounted.current) setLyricsLoading(false);
        });
        return () => { cancelled = true; };
    }, [phoneShowing, songId, tab]);

    // The dropdown's contents never change while the page is open, so this is
    // read once rather than every time the Platform tab is opened.
    useEffect(() => {
        let cancelled = false;
        getStreamingPlatforms().then((response) => {
            if (cancelled || !mounted.current) return;
            const list = (response && response.status === 200 && response.data.data) || [];
            setPlatformCatalog(list);
            setPlatformCatalogCopy(list);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // ---------------------------------------------------------------- saving

    // One field at a time: AddMusicDetailsModal commits on blur, so this is
    // called with the whole detail object each time a single field changes.
    // Only the columns this page owns are sent — a partial UPDATE leaves the
    // song's title, slug, tags, poster and platforms alone.
    const saveDetails = (postObj) => {
        const values = postObj && postObj.musicDetailData;
        if (!values || songId === undefined) return;

        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songLinkUpdate));
        dispatch(setSnackBarVariant('info'));

        const formData = new FormData();
        values.genre && formData.append('genre', values.genre);
        values.mood && formData.append('mood', values.mood);
        values.language && formData.append('language', values.language);
        values.isrc && formData.append('isrc', values.isrc);
        values.artists && formData.append('artists', values.artists);
        // Appended by presence like the fields above EXCEPT these three: `about`
        // and `record_label` are text a creator may want to empty, and
        // `is_explicit` is a boolean whose whole point is that `false` is a real
        // value — a truthiness check would silently drop all three when unset.
        if (values.about !== undefined) formData.append('about', values.about);
        if (values.record_label !== undefined) formData.append('record_label', values.record_label);
        if (values.is_explicit !== undefined) formData.append('is_explicit', values.is_explicit);
        // A DateField will not accept '', so an unset release date is skipped
        // rather than sent blank.
        values.release_date && formData.append('release_date', values.release_date);
        formData.append('songId', songId);
        formData.append('userId', userData.id);

        handleSongList(formData, 'UPDATE')
            .then((response) => {
                if (!mounted.current) return;
                if (response && (response.status === 200 || response.status === 201)) {
                    dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songLinkUpdateSuccess));
                    dispatch(setSnackBarVariant('success'));
                    // The form seeds its state once, on mount, and leaving the
                    // tab unmounts it. Without this re-read, coming back to
                    // Details would re-seed from the row as it was BEFORE the
                    // edit and show the old value in a field that saved fine.
                    loadDetail(true);
                } else {
                    dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
                    dispatch(setSnackBarVariant('error'));
                }
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2000);
            })
            .catch(() => {
                if (!mounted.current) return;
                dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
                dispatch(setSnackBarVariant('error'));
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2000);
            });
    };

    // Everything the profile toggle needs before it will turn on, in the order
    // a creator fills them — the same six the song card's switch checks, read
    // here from the song this page already has. The platform condition waits
    // for that read: an empty list that has not answered yet is not the same
    // as a song with no platforms, and blaming the creator for it is worse
    // than saying nothing.
    const filled = (value) => value !== undefined && value !== null && value !== '';
    const livePlatforms = platforms.filter((platform) => platform && platform.id).length;
    const visibilityBlockers = [
        !songTitle && 'a song title',
        song && !filled(song.artists) && 'an artist name',
        song && !filled(song.genre) && 'a genre',
        song && !filled(song.mood) && 'a mood',
        song && !filled(song.language) && 'a language',
        !platformsLoading && !livePlatforms && 'a streaming platform link',
    ].filter(Boolean);

    const sentenceList = (items) => (items.length < 2
        ? items.join('')
        : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

    const visibilityHint = songId === undefined
        ? 'Save this song first — the eye turns on once it exists.'
        : visibilityBlockers.length > 0
            ? `This song needs ${sentenceList(visibilityBlockers)} before it can go on your profile.`
            : visible
                ? 'Showing on your profile. Click to hide it.'
                : 'Hidden from your profile. Click to show it.';

    const toggleVisible = () => {
        if (songId === undefined || visibleSaving) return;
        const next = !visible;
        setVisible(next);
        setVisibleSaving(true);
        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songLinkUpdate));
        dispatch(setSnackBarVariant('info'));

        const formData = new FormData();
        formData.append('visible', next);
        formData.append('songId', songId);
        formData.append('userId', userData.id);

        handleSongList(formData, 'UPDATE')
            .then((response) => {
                if (!mounted.current) return;
                if (response && (response.status === 200 || response.status === 201)) {
                    dispatch(setSnackBarMessage(next
                        ? 'This song is now on your profile.'
                        : 'This song is hidden from your profile.'));
                    dispatch(setSnackBarVariant('success'));
                    // The row is what the eye is seeded from, so it has to carry
                    // the new value too — otherwise leaving and coming back
                    // shows the old state for a song that saved fine.
                    setRow((current) => (current ? { ...current, visible: next } : current));
                } else {
                    setVisible(!next);
                    dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
                    dispatch(setSnackBarVariant('error'));
                }
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2000);
            })
            .catch(() => {
                if (!mounted.current) return;
                setVisible(!next);
                dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
                dispatch(setSnackBarVariant('error'));
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2000);
            })
            .finally(() => { if (mounted.current) setVisibleSaving(false); });
    };

    const cameFrom = (location.state && location.state.from) || null;
    const backLabel = cameFrom ? `Back to ${cameFrom.label}` : 'Back to Song Hub';
    const goBack = () => history.push(
        cameFrom ? cameFrom.path : '/songhub',
        cameFrom ? { songId, songTitle } : undefined
    );

    // One field, one request, same endpoint as the detail form. `extra` is what
    // this particular save is changing; nothing else is sent, so a slug edit
    // cannot disturb the artist name and vice versa.
    const saveIdentity = (extra, onDone) => {
        if (songId === undefined) return;
        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songSlugUpdate));
        dispatch(setSnackBarVariant('info'));

        const formData = new FormData();
        Object.keys(extra).forEach((key) => formData.append(key, extra[key]));
        formData.append('songId', songId);
        formData.append('userId', userData.id);

        handleSongList(formData, 'UPDATE')
            .then((response) => {
                if (!mounted.current) return;
                const ok = response && (response.status === 200 || response.status === 201);
                if (ok) {
                    dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songSlugUpdateSuccess));
                    dispatch(setSnackBarVariant('success'));
                    setRow((current) => ({ ...current, ...extra }));
                } else {
                    // The slug is the one field the server rejects with a
                    // reason worth reading — "already taken" is actionable in a
                    // way "something went wrong" is not.
                    const body = (response && response.data) || {};
                    dispatch(setSnackBarMessage(
                        (body.slug && body.slug[0]) || formSnackbar.errors.errorMsg
                    ));
                    dispatch(setSnackBarVariant('error'));
                }
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2500);
                if (onDone) onDone(ok, response);
            })
            .catch(() => {
                if (!mounted.current) return;
                dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
                dispatch(setSnackBarVariant('error'));
                setTimeout(() => mounted.current && dispatch(setSnackBar(false)), 2000);
                if (onDone) onDone(false);
            });
    };

    const commitSlug = () => {
        const wanted = slugValue.trim();
        if (!wanted || wanted === (row && row.slug)) {
            setSlugValue((row && row.slug) || '');
            setSlugError('');
            return;
        }
        // Same shape the address itself allows. Caught here so a space or a
        // slash is answered instantly rather than by a 400.
        if (!/^[A-Za-z0-9][A-Za-z0-9-]*$/.test(wanted)) {
            setSlugError('Letters, numbers and hyphens only, starting with a letter or number.');
            return;
        }
        setSlugError('');
        saveIdentity({ slug: wanted }, (ok) => {
            if (!ok) return;
            // The address in the browser bar IS this slug. Left alone, a refresh
            // after renaming would look the song up under a name it no longer
            // has. `replace`, so Back still leaves the page rather than
            // returning to the dead address.
            history.replace({
                pathname: `/songhub/music/${wanted}`,
                search: location.search,
                state: location.state,
            });
        });
    };

    const commitTags = (event, value) => {
        const invalid = value.some((tag) => /<script/i.test(String(tag)));
        if (invalid) {
            setTagError('Please enter a valid tag name.');
            return;
        }
        setTagError('');
        setTags(value);
        saveIdentity({ tags: value.join(',') });
    };

    // ---------------------------------------------------------------- header

    const poster = posterOf(song);
    // The same shape the Song Hub header prints for the music page itself.
    const publicUrl = `${process.env.REACT_APP_CREATORURL}${(userData && userData.userHandle) || ''}/music/${linkSlug || ''}`;

    const breadcrumbs = [
        { label: 'Song Hub', link: '/songhub' },
        { label: 'Music', link: '/songhub' },
        { label: songTitle || 'Details', link: location.pathname },
    ];

    // Where you are and the way out, on the page itself rather than inside the
    // card. A page heading printed on the same white as the panel under it is a
    // caption for that panel; standing on the background, it titles the SCREEN —
    // which is what the back arrow beside it is leaving.
    const pageHead = (
        <>
            {/* The Song Hub header, one level down: title and breadcrumbs on the
                left, the quiet actions opposite. Same page, same spacing, same
                buttons — this is a screen of that section, not a different
                product. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    px: { xs: 0.5, md: 1 },
                    pt: { xs: 1, md: 1.5 },
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    {/* The arrow shares the TITLE's line rather than being
                        centred against the whole two-line block — centred, it
                        floated between the heading and the breadcrumbs and
                        belonged to neither. Bare, too: a bordered white tile is
                        the shape this page's ACTIONS wear, and a way out is not
                        one of them. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${BACK_GAP}px`, minWidth: 0 }}>
                        <Tooltip arrow title={backLabel}>
                            <Box
                                component="button"
                                type="button"
                                onClick={goBack}
                                aria-label={backLabel}
                                sx={{
                                    width: BACK_SIZE,
                                    height: BACK_SIZE,
                                    flexShrink: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 0,
                                    borderRadius: '50%',
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    color: palette.inkSoft,
                                    cursor: 'pointer',
                                    transition: `background-color ${motion.base}, color ${motion.base}`,
                                    '&:hover': { bgcolor: palette.brandSofter, color: palette.brand },
                                }}
                            >
                                <ArrowBackRoundedIcon sx={{ fontSize: 21 }} />
                            </Box>
                        </Tooltip>
                        <Typography component="h2" sx={{ ...typography.pageTitle, mb: 0 }}>
                            Song details
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, pl: `${BACK_SIZE + BACK_GAP}px` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={crumb.label}>
                                {index > 0 && (
                                    <Box component="span" sx={{ px: 1, color: palette.hint, fontSize: 10 }}>
                                        ◆
                                    </Box>
                                )}
                                <Link
                                    to={crumb.link}
                                    style={{ textDecoration: 'none', color: palette.muted, fontSize: '0.8rem' }}
                                >
                                    {crumb.label}
                                </Link>
                            </React.Fragment>
                        ))}
                    </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {/* On or off the public profile, in the one place that
                        knows every reason it might not go on. The card in the
                        list has the same switch; this is the same field, so
                        either screen answers for the other. */}
                    <Tooltip arrow title={visibilityHint}>
                        <span>
                            <GhostBtn
                                disabled={songId === undefined || visibilityBlockers.length > 0 || visibleSaving}
                                aria-label={visible ? 'Hide this song from your profile' : 'Show this song on your profile'}
                                aria-pressed={visible}
                                onClick={toggleVisible}
                                sx={{
                                    px: 1.25,
                                    ...(visible ? { color: palette.brand, borderColor: palette.brand } : {}),
                                }}
                            >
                                {visible
                                    ? <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                    : <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />}
                            </GhostBtn>
                        </span>
                    </Tooltip>

                    {/* Sharing is a sheet, not a second screen. The address, the
                        QR code and the alias are three things a creator reads in
                        one breath and then leaves — and leaving the song to read
                        them meant coming back to find which tab they were on.
                        The icon carries it: the label was a word explaining a
                        button that opens a panel titled with the same word. */}
                    <Tooltip arrow title="Share — short URL, QR code and custom address">
                        <span>
                            <GhostBtn
                                disabled={songId === undefined}
                                aria-label="Share this song's short URL"
                                onClick={() => setShareOpen(true)}
                                sx={{ px: 1.25 }}
                            >
                                <LinkRoundedIcon sx={{ fontSize: 18 }} />
                            </GhostBtn>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </>
    );

    const header = (
        <>
            {/* Which song this is, in the bar Song Hub uses for the music page's
                own address — poster, name, public link. The title is NOT in the
                page heading: a heading that changes with the record is a heading
                you have to read to know where you are. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: { xs: 1.5, md: 2.5 },
                    py: 1.5,
                    borderBottom: `1px solid ${palette.line}`,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: `${radius.sm}px`,
                        overflow: 'hidden',
                        bgcolor: palette.brandSoft,
                        color: palette.brand,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {poster
                        ? <img src={poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <TuneIcon sx={{ fontSize: 20 }} />}
                </Box>

                {/* `flex: 1 1 240px` with `minWidth: 0` is what lets a long
                    address truncate instead of shoving the buttons off the bar. */}
                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: palette.ink }} noWrap>
                        {songTitle || 'Untitled song'}
                    </Typography>
                    <Typography
                        component="a"
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={publicUrl}
                        sx={{
                            display: 'block',
                            fontSize: 12.5,
                            color: palette.muted,
                            textDecoration: 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transition: `color ${motion.base}`,
                            '&:hover': { color: palette.brand, textDecoration: 'underline' },
                        }}
                    >
                        {publicUrl}
                    </Typography>
                </Box>
            </Box>

            {/* Band two of the same panel. */}
            <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${palette.line}` }}>
            <Tabs
                value={tab}
                onChange={(event, value) => changeTab(value)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                    minHeight: 0,
                    // A segmented control, not an underline: a pill bar reads as
                    // one control with one thing chosen, which is what a tab
                    // strip of three or four short words actually is. Full width
                    // on a phone, only as wide as its own tabs above that.
                    display: { xs: 'flex', sm: 'inline-flex' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: '100%',
                    bgcolor: '#F3F4F6',
                    borderRadius: '999px',
                    p: '4px',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTabs-flexContainer': { gap: '4px' },
                    '& .MuiTab-root': {
                        minHeight: 34,
                        minWidth: 0,
                        py: 0,
                        px: { xs: 1.5, sm: 2 },
                        borderRadius: '999px',
                        fontSize: 13,
                        fontWeight: 700,
                        textTransform: 'none',
                        color: palette.muted,
                        transition: 'background-color 180ms cubic-bezier(0.4, 0, 0.2, 1), color 180ms',
                    },
                    '& .MuiTab-root:hover': { color: palette.ink },
                    '& .Mui-selected': {
                        bgcolor: '#FFFFFF',
                        color: `${palette.brand} !important`,
                        boxShadow: '0 1px 3px rgba(15, 23, 41, 0.12)',
                    },
                    '& .Mui-disabled': { opacity: 0.45 },
                }}
            >
                <Tab
                    value="details"
                    label="Details"
                    icon={<TuneIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                />
                <Tab
                    value="platform"
                    label="Platform"
                    icon={<LibraryMusicOutlinedIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    disabled={songId === undefined}
                />
                <Tab
                    value="playlist"
                    label="Playlist"
                    icon={<QueueMusicRoundedIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    disabled={songId === undefined}
                />
                <Tab
                    value="lyrics"
                    label="Lyrics"
                    icon={<NotesIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    disabled={songId === undefined}
                />
                <Tab
                    value="seo"
                    label="Search & social"
                    icon={<ManageSearchIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    disabled={songId === undefined}
                />
                <Tab
                    value="insights"
                    label="Insights"
                    icon={<InsightsOutlinedIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    disabled={songId === undefined}
                />
            </Tabs>
            </Box>
        </>
    );

    // The page wrapper Song Hub uses, so this screen sits on the same white
    // page with the same gutters. `pb-5` is what the last card lands on: the
    // app footer is `fixed-bottom`, an overlay rather than a block after the
    // page, so nothing scrolls clear of it on its own.
    // Form on the left, the song's public page on the right, the way every link
    // manager lays this screen out — you edit a field and watch the page it
    // belongs to change. The phone needs width to be worth having, so below
    // 1200px it steps aside and the Preview tab covers the same ground.
    // Built once and rendered twice — in the rail on a desktop, in the dialog
    // on anything narrower.
    const phone = (
        <PhonePreview
            song={song}
            poster={poster}
            platforms={platforms}
            panel={phoneShowing}
            onPanel={setPhonePanel}
            lyrics={lyrics}
            lyricsLoading={lyricsLoading}
            banner={userData && userData.banner_img
                ? `${process.env.REACT_APP_BASEURL_MEDIA}/media/${userData.banner_img}`
                : ''}
            genre={genre}
            moodList={moodList}
            languageList={languageList}
            loading={!song || detailLoading || listsLoading}
        />
    );

    const shell = (children) => (
        <div className="container-fluid py-4 creator-playlist-panel">
            <div className="row m-0">
                <div className="col-12 col-xl-8 pb-5">
                    {pageHead}

                    {/* ONE card. The song's address, the tab strip and the
                        panel the strip chooses are three bands of the same
                        thing, and three separate white boxes stacked with gaps
                        between them made the screen read as three unrelated
                        widgets that happened to be on the same page. The page
                        HEADING is not one of those bands — it names the screen,
                        so it stands on the background above them. */}
                    <Box
                        sx={{
                            mt: 2.5,
                            bgcolor: palette.surface,
                            border: `1px solid ${palette.line}`,
                            borderRadius: `${radius.md}px`,
                            boxShadow: shadow.card,
                            overflow: 'hidden',
                        }}
                    >
                        {header}
                        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>{children}</Box>
                    </Box>
                </div>
                <div className="col-xl-4 d-none d-xl-block">
                    <div className="phone-rail">{phone}</div>
                </div>
            </div>

            {/* The way back to the preview on a screen too narrow to keep it
                open. A pill rather than a round FAB: the word is the point —
                an eye alone at the foot of a form is a guess — and it sits
                above the fixed copyright bar rather than under it. Hidden from
                xl up, where the phone is already on screen and a button
                offering to show it would be offering nothing — which is at
                1200px, where the rail appears. */}
            <Box
                component="button"
                type="button"
                onClick={() => setPreviewOpen(true)}
                sx={{
                    // MUI's `xl` is 1536px; Bootstrap's `d-xl-block` — what
                    // puts the rail on screen — is 1200px, which is MUI's `lg`.
                    // Keyed to `xl` this button stayed up for the whole 336px
                    // between them, offering to show a phone already showing.
                    display: { xs: 'inline-flex', lg: 'none' },
                    alignItems: 'center',
                    gap: 0.75,
                    position: 'fixed',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: 64,
                    // Over the fixed copyright bar, under MUI's own drawers —
                    // the sidebar sits at 1200 and must stay on top of this.
                    zIndex: 1150,
                    px: 2,
                    py: 1,
                    border: `1px solid ${palette.line}`,
                    borderRadius: '999px',
                    bgcolor: palette.surface,
                    color: palette.ink,
                    fontSize: 13,
                    fontWeight: 700,
                    boxShadow: '0 8px 24px rgba(15, 23, 41, 0.16)',
                    cursor: 'pointer',
                    transition: `color ${motion.base}, border-color ${motion.base}`,
                    '&:hover': { color: palette.brand, borderColor: palette.brand },
                }}
            >
                <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                Preview
            </Box>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth={false}
                PaperProps={{
                    // The phone brings its own frame and shadow, so the dialog
                    // around it is nothing but a way to close it.
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible',
                        m: 2,
                    },
                }}
            >
                <IconButton
                    onClick={() => setPreviewOpen(false)}
                    aria-label="Close preview"
                    size="small"
                    sx={{
                        alignSelf: 'flex-end',
                        mb: 1,
                        bgcolor: palette.surface,
                        color: palette.inkSoft,
                        boxShadow: shadow.card,
                        '&:hover': { bgcolor: palette.brandSoft, color: palette.brand },
                    }}
                >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {phone}
            </Dialog>

            {/* Mounted with the page, but it asks gn2.me for nothing until it is
                opened — and asking IS what mints the link, so a sheet that
                loaded on render would hand a short URL to every song whose
                details were ever looked at. */}
            <ShareLinkDialog
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                surface={SHARE_SURFACE.SONG}
                songId={songId}
                subject={songTitle}
            />
        </div>
    );

    // ------------------------------------------------------ states before UI

    if (lookupFailed) {
        return shell(
            <EmptyState
                icon={<MusicOffRoundedIcon sx={{ fontSize: 32 }} />}
                title="That song could not be found"
                description={`Nothing in your Song Hub has the address "${slug}". It may have been renamed or deleted.`}
                action={<GhostBtn onClick={() => history.push('/songhub')}>Back to Song Hub</GhostBtn>}
            />
        );
    }

    if (!song) {
        return shell(
            <Stack spacing={2.5}>
                <Skeleton variant="rounded" height={128} />
                <Skeleton variant="rounded" height={220} />
            </Stack>
        );
    }

    // ------------------------------------------------------------- the panes

    const infoPane = (
        <SectionCard
            title="Info"
            subtitle="Where this song lives on your page, and how you group it."
            sx={{ mb: 2.5 }}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        className="w-100"
                        size="small"
                        label="Song slug"
                        value={slugValue}
                        onChange={(event) => setSlugValue(event.target.value)}
                        onBlur={commitSlug}
                        onKeyDown={(event) => { if (event.key === 'Enter') event.target.blur(); }}
                        error={Boolean(slugError)}
                        helperText={slugError || 'The last part of this song\u2019s public address.'}
                        disabled={isDisabled}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                    <Box component="span" sx={{ fontSize: 13, color: palette.hint, fontWeight: 700 }}>/</Box>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={tagOptions.map((option) => option.tagName)}
                        value={tags}
                        onChange={commitTags}
                        disabled={isDisabled}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    size="small"
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={option}
                                    sx={{
                                        height: 22,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        bgcolor: palette.brandSoft,
                                        color: '#B3262C',
                                    }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tags"
                                error={Boolean(tagError)}
                                helperText={tagError || 'Type a tag and press Enter. Saved as you go.'}
                                disabled={isDisabled}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </SectionCard>
    );

    const detailsPane = (
        <SectionCard
            title="Details"
            subtitle="Every field saves on its own, the moment you leave it. Genre, mood, language and artist are what let this song be shown on your profile."
        >
            {/* The form seeds its state once, on mount, so it is not mounted
                until BOTH the detail read and the dropdown lists are in — given
                the thin row first it would leave the release fields blank, and
                given empty lists it would render a Select whose saved value
                matches no option. */}
            {detailLoading || listsLoading
                ? <Skeleton variant="rounded" height={320} />
                : (
                    <AddMusicDetailsModal
                        data={song}
                        compareId={songId}
                        createSong={saveDetails}
                        updateSong={saveDetails}
                        genre={genre}
                        moodList={moodList}
                        languageList={languageList}
                    />
                )}
        </SectionCard>
    );

    // Lyrics and Search & social carry their own heading and description, so
    // the card around them is left untitled rather than repeating it.
    const lyricsPane = (
        <SectionCard>
            <SongLyricsEditor
                songId={songId}
                languageList={languageList}
                disabled={isDisabled}
            />
        </SectionCard>
    );

    const seoPane = (
        <SectionCard>
            <SongSeoFields songId={songId} disabled={isDisabled} />
        </SectionCard>
    );

    const platformPane = (
        <PlatformPane
            songId={songId}
            disabled={isDisabled}
            rows={platforms}
            setRows={setPlatforms}
            catalog={platformCatalog}
            setCatalog={setPlatformCatalog}
            catalogCopy={platformCatalogCopy}
            loading={platformsLoading}
            reload={loadPlatforms}
        />
    );

    // Mounted only while its tab is showing, which is what re-reads the
    // playlists — and which of them hold this song — each time the creator
    // comes back to look.
    const playlistPane = <PlaylistPane songId={songId} />;

    // The numbers gn2.me records against this song's short URL, on the song's
    // own screen. The very component the short-URL page's Analytics tab renders,
    // so the two can never drift into telling a creator different things — and
    // it probes for the link rather than asking for one, so opening this tab
    // never mints a gn2.me address nobody asked for.
    const insightsPane = (
        <LinkAnalytics
            surface={SHARE_SURFACE.SONG}
            songId={songId}
            onCreate={() => history.push(
                `/songhub/music/${linkSlug}/short-url`,
                { songId, songTitle, from: { path: location.pathname, label: 'Song details' } }
            )}
        />
    );

    return shell(
        // Full width. A 900px column left a third of the screen empty on the
        // desktop this is edited on, and the fields inside are already gridded
        // two-up, so the cap bought nothing but the gap.
        <Box>
            {tab === 'details' && <>{infoPane}{detailsPane}</>}
            {tab === 'platform' && platformPane}
            {tab === 'playlist' && playlistPane}
            {tab === 'lyrics' && lyricsPane}
            {tab === 'seo' && seoPane}
            {tab === 'insights' && insightsPane}
        </Box>
    );
};

export default SongDetailsPage;
